import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Modal, Alert, Spin, message } from 'antd';
import { MessageOutlined, RobotOutlined, SettingOutlined } from '@ant-design/icons';
import { usePermissions } from '../../hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import ChatInterfaceWithHistory from '../ChatInterfaceWithHistory';
import { getAllChatbotConfigs } from '../../api/chatbot/ChatbotApi';

// AYD Content Component
const AydContent = () => {
  const [iframeUrl, setIframeUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef(null);

  const fetchAydSession = useCallback(async () => {
    setLoadingMessage('🤖 Initializing AYD chatbot...');
    
    // Check if we have a cached session URL that's still valid
    const cachedSession = localStorage.getItem('ayd_session_url');
    const cacheTime = localStorage.getItem('ayd_session_time');
    const now = Date.now();
    
    // Use cached session if it's less than 30 minutes old
    if (cachedSession && cacheTime && (now - parseInt(cacheTime)) < 30 * 60 * 1000) {
      setLoadingMessage('🎯 Restoring your AYD session...');
      return cachedSession;
    }
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}v1/chatbot/ayd/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify({}),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to initialize AYD chatbot');
    }

    // Cache the session URL
    localStorage.setItem('ayd_session_url', data.url);
    localStorage.setItem('ayd_session_time', now.toString());

    setLoadingMessage('🎯 Connecting to your database...');
    return data.url;
  }, []);

  const handleIframeLoad = () => {
    setIframeLoaded(true);
    setIsLoading(false);
    setLoadingMessage('');
  };

  const clearAydSession = () => {
    localStorage.removeItem('ayd_session_url');
    localStorage.removeItem('ayd_session_time');
  };

  useEffect(() => {
    const initializeAyd = async () => {
      setIsLoading(true);
      setIframeLoaded(false);
      setLoadingMessage('🖥️ AYD is getting things ready for you...');
      
      try {
        // First, try to get the chatbot ID from the configuration
        const configs = await getAllChatbotConfigs();
        const aydConfig = configs?.find(config => config.botType === 'AYD' && config.enabled);
        
        if (!aydConfig) {
          throw new Error('AYD configuration not found');
        }
        
        // Start with the direct chatbot URL (as per AYD documentation)
        const directUrl = `https://www.askyourdatabase.com/chatbot/${aydConfig.chatbotid}`;
        setIframeUrl(directUrl);
        
        // Stop loading after a short delay
        setTimeout(() => {
          setIsLoading(false);
        }, 2000);
        
      } catch (error) {
        console.error('Failed to load AYD chatbot:', error);
        setLoadingMessage('');
        setIsLoading(false);
        if (error.message?.includes('not enabled')) {
          message.info('AYD chatbot is not enabled for this organization');
        } else {
          message.error('Failed to initialize AYD chatbot. Please try again later.');
        }
      }
    };

    // Listen for messages from the iframe (as per AYD documentation)
    const handleMessage = (event) => {
      if (event.origin !== 'https://www.askyourdatabase.com') {
        return;
      }
      
      if (event.data.type === 'LOGIN_SUCCESS') {
        // Login successful - update iframe URL with the authenticated session URL
        setIframeUrl(event.data.url);
        setIsLoading(false);
      } else if (event.data.type === 'LOGIN_REQUIRED') {
        // Login required - fetch a new authenticated session from our API endpoint
        fetchAydSession().then(url => {
          setIframeUrl(url);
          setIsLoading(false);
        }).catch(error => {
          console.error('Failed to fetch AYD session:', error);
          setIsLoading(false);
        });
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('message', handleMessage);
    }

    initializeAyd();

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('message', handleMessage);
      }
    };
  }, [fetchAydSession]);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        background: '#000000',
        color: '#00ff00',
        padding: '40px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Matrix rain effect */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          zIndex: 1
        }}>
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                color: '#00ff00',
                fontSize: '12px',
                fontFamily: 'monospace',
                animation: `matrix-rain ${2 + Math.random() * 3}s linear infinite`,
                animationDelay: `${Math.random() * 2}s`,
                opacity: 0.7
              }}
            >
              {Math.random() > 0.5 ? '1' : '0'}
            </div>
          ))}
        </div>
        
        <div style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px', textShadow: '0 0 10px #00ff00' }}>🖥️</div>
          <div style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            marginBottom: '10px', 
            textAlign: 'center',
            textShadow: '0 0 10px #00ff00',
            fontFamily: 'monospace'
          }}>
            AYD - Ask Your Database
          </div>
          <div style={{ 
            fontSize: '16px', 
            textAlign: 'center', 
            marginBottom: '30px',
            textShadow: '0 0 5px #00ff00',
            fontFamily: 'monospace'
          }}>
            {loadingMessage}
          </div>
          <div style={{ 
            fontSize: '14px', 
            marginTop: '20px', 
            opacity: 0.8, 
            textAlign: 'center',
            textShadow: '0 0 5px #00ff00',
            fontFamily: 'monospace'
          }}>
            Thank you for your patience
          </div>
        </div>
        
        <div style={{
          position: 'absolute',
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '2px solid #00ff00',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            boxShadow: '0 0 10px #00ff00'
          }} />
        </div>
       
        <style>
          {`
            @keyframes matrix-rain {
              0% {
                transform: translateY(-100px);
                opacity: 0;
              }
              10% {
                opacity: 0.7;
              }
              90% {
                opacity: 0.7;
              }
              100% {
                transform: translateY(100vh);
                opacity: 0;
              }
            }
            
            @keyframes spin {
              0% { transform: translateX(-50%) rotate(0deg); }
              100% { transform: translateX(-50%) rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  if (iframeUrl) {
    return (
      <iframe
        ref={iframeRef}
        allow="clipboard-read; clipboard-write"
        className="mx-auto"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          borderRadius: '8px',
        }}
        src={iframeUrl}
        title="AYD - Ask Your Database"
        onLoad={handleIframeLoad}
      />
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100%',
      background: '#000000',
      color: '#00ff00',
      padding: '40px'
    }}>
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>🖥️</div>
      <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>
        AYD - Ask Your Database
      </div>
      <div style={{ fontSize: '16px', textAlign: 'center' }}>
        Preparing your personalized database experience...
      </div>
    </div>
  );
};

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [aydConfig, setAydConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatMode, setChatMode] = useState('GEM'); // Default to G.E.M
  const { hasChatbotPermission } = usePermissions();
  const navigate = useNavigate();

  useEffect(() => {
    checkAydConfiguration();
  }, []);

  const checkAydConfiguration = async () => {
    try {
      setLoading(true);
      // Check for AYD configurations in the main chatbot configs
      const configs = await getAllChatbotConfigs();
      const aydConfigs = configs?.filter(config => config.botType === 'AYD' && config.enabled);
      
      if (aydConfigs && aydConfigs.length > 0) {
        setAydConfig(aydConfigs[0]);
        setChatMode('AYD');
      } else {
        setChatMode('GEM');
      }
    } catch (error) {
      // No configurations found, default to G.E.M
      setChatMode('GEM');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChatbot = () => {
    setIsOpen(true);
  };

  const handleCloseChatbot = () => {
    setIsOpen(false);
  };

  const handleGoToSettings = () => {
    setIsOpen(false);
    navigate('/settings/general/chatbot');
  };

  // Don't render the widget if user doesn't have chatbot permission
  if (!hasChatbotPermission) {
    return null;
  }

  return (
    <>
      {/* Floating Chat Button */}
      <div
        style={{
          position: 'fixed',
          bottom: '14px',
          right: '24px',
          zIndex: 1000,
        }}
      >
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={loading ? <Spin size="small" /> : <MessageOutlined style={{ fontSize: '24px' }} />}
          onClick={handleOpenChatbot}
          disabled={loading}
          style={{
            width: '64px',
            height: '64px',
            background: '#F15A24',
            border: 'none',
            boxShadow: '0 4px 16px rgba(241, 90, 36, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(241, 90, 36, 0.6)';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(241, 90, 36, 0.4)';
            }
          }}
        />
      </div>

      {/* Chatbot Modal */}
      <Modal
        title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            background: '#FFE3DB',
            margin: '-20px -24px 0',
            padding: '16px 24px',
            borderRadius: '8px 8px 0 0',
            color: '#F15A24'
          }}>
            <RobotOutlined style={{ fontSize: '24px', marginRight: '12px' }} />
            <div>
              {chatMode === 'AYD' ? (
                <>
                  <div style={{ fontSize: '18px', fontWeight: '600' }}>AYD</div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>Ask Your Database</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '18px', fontWeight: '600' }}>G.E.M</div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>Goal Execution Manager</div>
                </>
              )}
            </div>
          </div>
        }
        open={isOpen}
        onCancel={handleCloseChatbot}
        footer={null}
        width={1200}
        style={{ top: 20 }}
        bodyStyle={{ 
          padding: 0, 
          height: 'calc(100vh - 120px)',
          maxHeight: '700px',
          borderRadius: '0 0 8px 8px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        styles={{
          header: { 
            padding: 0,
            border: 'none',
            background: 'transparent'
          }
        }}
      >
        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '400px' 
          }}>
            <Spin size="large" />
          </div>
        ) : chatMode === 'AYD' && aydConfig ? (
          <AydContent />
        ) : chatMode === 'GEM' ? (
          <ChatInterfaceWithHistory botType="GEM" />
        ) : (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Alert
              type="warning"
              showIcon
              message="Chatbot Not Configured"
              description={
                <div>
                  <p>No chatbot is currently configured for your organization.</p>
                  <p>Please contact your administrator to set up either:</p>
                  <ul style={{ textAlign: 'left', marginTop: '16px' }}>
                    <li><strong>G.E.M</strong> - Internal AI assistant for strategic planning</li>
                    <li><strong>AYD</strong> - Database querying with natural language</li>
                  </ul>
                </div>
              }
              action={
                <Button 
                  type="primary" 
                  icon={<SettingOutlined />}
                  onClick={handleGoToSettings}
                >
                  Go to Settings
                </Button>
              }
              style={{ margin: '20px' }}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default ChatbotWidget; 